import { addEventListenerWithKey } from "../../shared/lib/utils.js"
import { onBFCacheLoad } from "../../shared/scripts/back_forward_cache_utils"

const BuildAutomationDropdown = async () => {
  // Cache DOM elements for better performance
  const dropdowns = document.querySelectorAll(".service-dropdown")
  const createButton = document.getElementById("create-applet-btn") as HTMLAnchorElement

  // Dropdown functionality for Build Automation Module
  dropdowns.forEach((dropdown, dropdownIndex) => {
    const dropdownDisplay = dropdown.querySelector(".dropdown-display")
    const dropdownMenu = dropdown.querySelector(".dropdown-menu")

    if (!dropdownDisplay || !dropdownMenu) return

    // Toggle dropdown on click
    const toggleHandler = (event: Event) => {
      event.preventDefault()
      event.stopPropagation()

      // Don't toggle if clicking on dropdown menu items
      const target = event.target as Element
      if (target.closest(".dropdown-menu")) {
        return
      }

      const isOpen = dropdown.classList.contains("open")

      // Close all other dropdowns using cached elements
      dropdowns.forEach(otherDropdown => {
        if (otherDropdown !== dropdown && otherDropdown.classList.contains("open")) {
          otherDropdown.classList.remove("open")
        }
      })

      // Toggle current dropdown
      dropdown.classList.toggle("open", !isOpen)
    }

    addEventListenerWithKey(dropdown, "click", `build_automation_dropdown_toggle_${dropdownIndex}`, toggleHandler)

    // Handle dropdown item selection
    dropdown.querySelectorAll(".dropdown-item").forEach((item, itemIndex) => {
      const itemSelectionHandler = (event: Event) => {
        event.preventDefault()
        event.stopPropagation()

        const tqaTextElement = dropdownDisplay.querySelector(".tqa-text")
        const serviceIconElement = dropdownDisplay.querySelector(".service-icon")

        if (tqaTextElement && item instanceof HTMLElement) {
          const itemTextElement = item.querySelector(".item-text")
          const itemText = itemTextElement?.textContent?.trim()
          if (itemText) {
            tqaTextElement.textContent = itemText
          }
        }

        // Update service icon if provided
        if (serviceIconElement instanceof HTMLElement && item instanceof HTMLElement) {
          const currentIcon = serviceIconElement.querySelector("img")

          try {
            const tqaData = JSON.parse(item.dataset.tqa || "{}")
            const service = tqaData.service

            if (currentIcon && service?.imageUrl) {
              currentIcon.src = service.imageUrl
              currentIcon.alt = service.id || ""
            }
            if (service?.brandColor) {
              serviceIconElement.style.backgroundColor = `#${service.brandColor}`
            }
          } catch (error) {
            console.error("Error parsing TQA data for icon update:", error)
          }
        }

        // Close dropdown after selection
        dropdown.classList.remove("open")

        // Update Create Applet button URL
        updateCreateAppletButton()
      }

      addEventListenerWithKey(
        item,
        "click",
        `build_automation_dropdown_item_${dropdownIndex}_${itemIndex}`,
        itemSelectionHandler,
      )
    })
  })

  // Close dropdowns when clicking outside
  const outsideClickHandler = (event: Event) => {
    const target = event.target as Element
    if (!target.closest(".service-dropdown")) {
      dropdowns.forEach(dropdown => {
        if (dropdown.classList.contains("open")) {
          dropdown.classList.remove("open")
        }
      })
    }
  }

  addEventListenerWithKey(document, "click", "build_automation_dropdown_outside_click", outsideClickHandler)

  // Close dropdowns on Escape key
  const keydownHandler = (event: Event) => {
    const keyboardEvent = event as KeyboardEvent
    if (keyboardEvent.key === "Escape") {
      dropdowns.forEach(dropdown => {
        if (dropdown.classList.contains("open")) {
          dropdown.classList.remove("open")
        }
      })
    }
  }

  addEventListenerWithKey(document, "keydown", "build_automation_dropdown_keydown", keydownHandler)

  // Function to get selected TQA data from a dropdown
  function getSelectedTQAData(dropdown: Element | null): {
    type: string
    full_module_name: string
    name: string
    service: { id: string; name?: string; brandColor: string; imageUrl: string }
  } | null {
    if (!dropdown) return null

    const dropdownDisplay = dropdown.querySelector(".dropdown-display")
    const tqaText = dropdownDisplay?.querySelector(".tqa-text")?.textContent?.trim()

    if (!tqaText) return null

    // Find the selected item based on text content
    const selectedItem = Array.from(dropdown.querySelectorAll(".dropdown-item")).find(item => {
      const itemText = item.querySelector(".item-text")?.textContent?.trim()
      return itemText === tqaText
    }) as HTMLElement

    if (!selectedItem?.dataset.tqa) return null

    try {
      // Parse JSON data from the data-tqa attribute
      const tqaData = JSON.parse(selectedItem.dataset.tqa)

      return {
        type: tqaData.type,
        full_module_name: tqaData.fullModuleName,
        name: tqaData.name,
        service: tqaData.service,
      }
    } catch (error) {
      console.error("Error parsing TQA data:", error)
      return null
    }
  }

  // The server renders triggers.first/actions.first as the initial selection;
  // snapshot them so the URL can say whether the user changed either dropdown.
  const defaultTrigger = getSelectedTQAData(document.querySelector("[data-dropdown='trigger']"))
  const defaultAction = getSelectedTQAData(document.querySelector("[data-dropdown='action']"))

  // Function to update the Start Building button URL
  function updateCreateAppletButton(): void {
    const triggerDropdown = document.querySelector("[data-dropdown='trigger']")
    const actionDropdown = document.querySelector("[data-dropdown='action']")

    if (!triggerDropdown || !actionDropdown || !createButton) return

    const selectedTrigger = getSelectedTQAData(triggerDropdown)
    const selectedAction = getSelectedTQAData(actionDropdown)

    if (selectedTrigger && selectedAction) {
      const url = new URL(createButton.dataset.appletCreate || "/create", window.location.origin)
      url.searchParams.set("template_trigger", selectedTrigger.full_module_name)
      url.searchParams.set("template_actions", selectedAction.full_module_name)

      // Chat prefill: the chat pane on /create builds the prefilled
      // prompt from these; users without chat access fall back to the template flow.
      const triggerChanged = selectedTrigger.full_module_name !== defaultTrigger?.full_module_name
      const actionChanged = selectedAction.full_module_name !== defaultAction?.full_module_name
      if (selectedTrigger.service?.name && selectedAction.service?.name) {
        url.searchParams.set("chat_prefill_trigger", selectedTrigger.name)
        url.searchParams.set("chat_prefill_trigger_service", selectedTrigger.service.name)
        url.searchParams.set("chat_prefill_action", selectedAction.name)
        url.searchParams.set("chat_prefill_action_service", selectedAction.service.name)
        url.searchParams.set(
          "chat_prefill_changed",
          triggerChanged && actionChanged ? "both" : triggerChanged ? "trigger" : actionChanged ? "action" : "none",
        )
      }

      createButton.href = url.toString()
    }
  }

  // Stamp the prefill params onto the default selection too — the server href
  // carries only the template params.
  updateCreateAppletButton()

  onBFCacheLoad("build_automation_dropdown", () => {
    BuildAutomationDropdown()
  })
}

export default BuildAutomationDropdown
